import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { OrgAppbarComponent } from '../../../layout/components/org-appbar/org-appbar.component';
import { FeatureFlagsService } from './services/feature-flags.service';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { takeUntil } from 'rxjs';

export interface FlagDefinition {
  key: string;
  label: string;
  description: string;
}

@Component({
  selector: 'app-feature-flags',
  standalone: true,
  imports: [OrgAppbarComponent, ReactiveFormsModule, ButtonModule, InputsModule, LabelModule, LayoutModule],
  templateUrl: './feature-flags.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureFlagsComponent extends BaseComponent implements OnInit {
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly route = inject(ActivatedRoute);
  private organizationId = 0;
  public isLoading = signal(true);

  public readonly flagDefinitions: FlagDefinition[] = [
    { key: 'vip_support', label: 'VIP Support', description: 'Show VIP Support button in customer navigation bar' },
    {
      key: 'support_bridge',
      label: 'Support Bridge',
      description: 'Use Support Bridge widget instead of Zoho for customer support',
    },
    {
      key: 'cases_v2',
      label: 'Cases v2',
      description: 'Enable the new Cases interface (replaces legacy Zoho Desk link + Case Log)',
    },
    { key: 'itdr', label: 'ITDR Dashboard', description: 'Enable the Identity Threat Detection & Response dashboard' },
    {
      key: 'itdr_elastic',
      label: 'ITDR-Elastic',
      description: 'Provision an Elastic workspace for this organization to ingest ITDR data sources.',
    },
    {
      key: 'policy_analysis',
      label: 'Policy Analysis',
      description: 'Enable the Policy Analysis nav item (Currently Prototype)',
    },
    {
      key: 'inventory',
      label: 'Asset Intelligence',
      description: 'Enable the Entity Invetory nav item (Currently Prototype',
    },
    {
      key: 'data_house',
      label: 'Data House',
      description:
        'Enable the Data House nav item for historical alert search and threat hunting (Currently Prototype)',
    },
  ];

  public flagsForm = new FormGroup(
    Object.fromEntries(this.flagDefinitions.map((f) => [f.key, new FormControl<boolean>(false)])) as Record<
      string,
      FormControl<boolean>
    >
  );

  ngOnInit(): void {
    this.organizationId = Number(this.route.snapshot.queryParamMap.get('organizationId'));
    this.featureFlagsService
      .getFlags(this.organizationId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res) => {
        const patch = Object.fromEntries(this.flagDefinitions.map((f) => [f.key, res.flags[f.key] ?? false]));
        this.flagsForm.patchValue(patch);
        this.flagsForm.markAsPristine();
        this.isLoading.set(false);
      });
  }

  save(): void {
    if (this.flagsForm.dirty && this.organizationId) {
      const flags = Object.fromEntries(
        this.flagDefinitions.map((f) => [f.key, this.flagsForm.get(f.key)?.value ?? false])
      );
      this.featureFlagsService
        .updateFlags(this.organizationId, { flags })
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          this.flagsForm.markAsPristine();
        });
    }
  }
}
